"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";
import { X } from "lucide-react";
import type { AniTheme } from "../tokens";
import AniCard from "../primitives/AniCard";
import AniIcon from "../primitives/AniIcon";
import AniButton from "../primitives/AniButton";

const HeatmapPlot = dynamic(() => import("./HeatmapPlot"), { ssr: false });
export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const HOURS = ["00:00", "03:00", "06:00", "09:00", "12:00", "15:00", "18:00", "21:00"];

function ChartTable({ data, id }: { data: number[][]; id: string }) {
  return <div id={id} className="ani-chart-table-wrap"
    role="region" aria-label="Activity data table" tabIndex={0}>
    <table className="ani-chart-table">
      <caption>Activity by weekday and time · number of events</caption>
      <thead><tr><th scope="col">Day</th>{HOURS.map((hour) => <th key={hour} scope="col">{hour}</th>)}</tr></thead>
      <tbody>{DAYS.map((day, row) => <tr key={day}><th scope="row">{day}</th>
        {HOURS.map((hour, col) => <td key={hour}>{data[row]?.[col] ?? 0}</td>)}
      </tr>)}</tbody>
    </table>
  </div>;
}

const AniChart = ({ data, theme }: { data: number[][]; theme: AniTheme }) => {
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!expanded) return;
    const dialog = dialogRef.current;
    const trigger = triggerRef.current;
    if (!dialog) return;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      trigger?.focus({ preventScroll: true });
    };
  }, [expanded]);

  useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  const downloadData = (format: "csv" | "xml") => {
    const content = format === "csv"
      ? [
        ["Day", ...HOURS].join(","),
        ...DAYS.map((day, row) => [day, ...(data[row] ?? [])].join(",")),
      ].join("\n")
      : [
        "<?xml version=\"1.0\" encoding=\"UTF-8\"?>",
        "<activity>",
        ...DAYS.map((day, row) => `  <day name=\"${day}\">${(data[row] ?? []).map((value, col) => `<event time=\"${HOURS[col]}\">${value}</event>`).join("")}</day>`),
        "</activity>",
      ].join("\n");
    const blob = new Blob([content], { type: format === "csv" ? "text/csv" : "application/xml" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `animalia-activity.${format}`;
    link.click();
    URL.revokeObjectURL(url);
    setMenuOpen(false);
  };

  return <>
    <AniCard className="ani-chart-card">
      <div className="ani-chart-head" data-structure="Chart header" data-gap="spacing/xs">
        <h4 id={`${id}-title`} className="ani-chart-title">Activity</h4>
        <div className="ani-chart-actions">
          <button ref={triggerRef} type="button" className="ani-icon-btn ani-chart-header-button" data-token="Ani Button/Tertiary"
            aria-haspopup="dialog" aria-controls={`${id}-modal`} aria-label="View data table"
            onClick={() => setExpanded(true)}><AniIcon name="table1" /></button>
          <div ref={menuRef} className="ani-chart-menu-wrap">
            <button type="button" className="ani-icon-btn ani-chart-header-button" data-token="Ani Button/Tertiary"
              aria-haspopup="menu" aria-expanded={menuOpen} aria-controls={`${id}-menu`} aria-label="Chart options"
              onClick={() => setMenuOpen((value) => !value)}><AniIcon name="dots-three" /></button>
            {menuOpen && <div id={`${id}-menu`} className="ani-chart-menu" role="menu" aria-label="Chart export options">
              <button type="button" role="menuitem" onClick={() => downloadData("csv")}>Export CSV</button>
              <button type="button" role="menuitem" onClick={() => downloadData("xml")}>Export XML</button>
            </div>}
          </div>
        </div>
      </div>
      <figure className="ani-chart-figure" aria-labelledby={`${id}-title`} data-token="Ani Chart/Heat map"
        data-inspect-tokens="--ani-chart-categorical-01 --ani-chart-footer-caption">
        <HeatmapPlot data={data} theme={theme} days={DAYS} hours={HOURS} />
        <figcaption className="sr-only">Activity by weekday and time. Darker cells mean more events. Use View data table to read every value.</figcaption>
      </figure>
    </AniCard>
    {/* Portalled to <body>: `.ani-stage-inner` (container-type) and the GSAP
        transforms above it would otherwise be the fixed dialog's containing
        block, centring it on the card rather than on the screen. The wrapper
        re-establishes the token scope the modal styles are written against. */}
    {expanded && createPortal(
      <div className="ani-modal-portal" data-ani-scope data-ani-theme={theme.label} style={theme.vars as React.CSSProperties}>
        <dialog ref={dialogRef} id={`${id}-modal`} className="ani-chart-modal" aria-labelledby={`${id}-modal-title`}
          onCancel={() => setExpanded(false)} onClose={() => setExpanded(false)}
          onClick={(event) => {
            if (event.target !== event.currentTarget) return;
            const rect = event.currentTarget.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) setExpanded(false);
          }}>
          <header className="ani-modal-header">
            <h3 id={`${id}-modal-title`}>Activity</h3>
            <button type="button" className="ani-icon-btn ani-modal-close" aria-label="Close data table" onClick={() => setExpanded(false)} autoFocus>
              <X size={24} aria-hidden="true" />
            </button>
          </header>
          <div className="ani-modal-content">
            <ChartTable data={data} id={`${id}-table`} />
          </div>
          <footer className="ani-modal-footer">
            <AniButton variant="secondary" onClick={() => downloadData("csv")}>Export CSV</AniButton>
            <AniButton onClick={() => setExpanded(false)}>Close</AniButton>
          </footer>
        </dialog>
      </div>, document.body)}
  </>;
};

export default AniChart;
